import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const LAST_SEEN_KEY = 'seva_inbox_last_seen';

function fmt(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function fmtDate(d) {
  return new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fmtCreated(str) {
  const d = new Date(str);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function getUnreadCount(reservations) {
  const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
  if (!lastSeen) return reservations.filter(r => r.source === 'ai').length;
  return reservations.filter(r => r.source === 'ai' && new Date(r.created_at) > new Date(lastSeen)).length;
}

export function markInboxSeen() {
  localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
}

const STATUS_COLOR = { confirmed: '#059669', pending: '#d97706', cancelled: '#94a3b8' };

export default function Inbox() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);

  const load = useCallback(async () => {
    try {
      const all = await api.getReservations({ source: 'ai', limit: 100 });
      // Sort newest first
      setReservations([...all].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (_) {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    markInboxSeen();
    window.addEventListener('seva:refresh', load);
    return () => window.removeEventListener('seva:refresh', load);
  }, [load]);

  const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
  const isNew = r => !lastSeen || new Date(r.created_at) > new Date(lastSeen);

  return (
    <div>
      <h1 style={s.h1}>AI Bookings</h1>
      <p style={s.sub}>Every reservation made by Seva appears here so nothing gets missed.</p>

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</div>
      ) : reservations.length === 0 ? (
        <div style={s.empty}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <div style={s.emptyText}>No AI bookings yet</div>
          <div style={s.emptySub}>When a customer books through the chatbot it'll appear here.</div>
        </div>
      ) : (
        <div style={s.list}>
          {reservations.map(r => (
            <div key={r.id} style={{ ...s.card, ...(isNew(r) ? s.cardNew : {}) }} onClick={() => setSelected(r)}>
              {isNew(r) && <div style={s.newDot} />}
              <div style={s.cardTop}>
                <div style={s.name}>{r.customer_name}</div>
                <div style={{ ...s.status, color: STATUS_COLOR[r.status] || '#94a3b8' }}>{r.status}</div>
              </div>
              <div style={s.meta}>
                <span>{fmtDate(r.date)} · {fmt(r.time_slot)}</span>
                <span>{r.party_size} guest{r.party_size !== 1 ? 's' : ''}</span>
                <span style={s.conf}>#{r.confirmation_number}</span>
              </div>
              <div style={s.created}>Booked {fmtCreated(r.created_at)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTop}>
              <div>
                <div style={s.modalName}>{selected.customer_name}</div>
                <div style={s.modalConf}>#{selected.confirmation_number}</div>
              </div>
              <button style={s.xBtn} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={s.divider} />
            <div style={s.grid}>
              <Row label="Date"    value={fmtDate(selected.date)} />
              <Row label="Time"    value={fmt(selected.time_slot)} />
              <Row label="Party"   value={`${selected.party_size} guests`} />
              <Row label="Phone"   value={selected.phone} />
              {selected.email && <Row label="Email" value={selected.email} />}
              {selected.dietary_restrictions && <Row label="Dietary" value={selected.dietary_restrictions} />}
              {selected.special_requests     && <Row label="Notes"   value={selected.special_requests} />}
              <Row label="Status"  value={selected.status} />
              <Row label="Booked"  value={fmtCreated(selected.created_at)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 14, color: '#0f172a', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

const s = {
  h1:       { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:      { fontSize: 14, color: '#64748b', marginTop: 4, marginBottom: 26 },
  list:     { display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 680 },
  card:     { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '16px 20px', cursor: 'pointer', position: 'relative', transition: 'box-shadow 0.15s', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' },
  cardNew:  { borderColor: '#c7d2fe', background: '#fafbff' },
  newDot:   { position: 'absolute', top: 16, right: 16, width: 8, height: 8, borderRadius: '50%', background: '#4f46e5' },
  cardTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  name:     { fontWeight: 700, fontSize: 15, color: '#0f172a' },
  status:   { fontSize: 12, fontWeight: 600, textTransform: 'capitalize' },
  meta:     { display: 'flex', gap: 14, fontSize: 13, color: '#475569', flexWrap: 'wrap' },
  conf:     { color: '#94a3b8', fontFamily: 'monospace' },
  created:  { fontSize: 12, color: '#94a3b8', marginTop: 6 },
  empty:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '60px 0', color: '#94a3b8' },
  emptyText:{ fontSize: 16, fontWeight: 600, color: '#cbd5e1' },
  emptySub: { fontSize: 13, color: '#94a3b8' },

  overlay:  { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 },
  modal:    { background: '#fff', borderRadius: 20, padding: '28px 32px', width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(15,23,42,0.18)' },
  modalTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  modalName:{ fontSize: 18, fontWeight: 800, color: '#0f172a' },
  modalConf:{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', marginTop: 4 },
  xBtn:     { background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 },
  divider:  { height: 1, background: '#f1f5f9', margin: '0 0 4px' },
  grid:     { display: 'flex', flexDirection: 'column' },
};
